import { PuzzleMapImporter } from './game/util/PuzzleMapImporter';
import { BlockGenAlgorithm, ExtendedMapDef, MapDef } from './game/defs/map';
import { AssetGroup, loader } from '@play-co/timestep-core';
import StateObserver, { user } from 'src/StateObserver';
import { match2MaxLevel } from 'src/replicant/match2/match2.ruleset';
import { isEditorMode } from './editor/editor.utils';
import {
  getLeagueLevel,
  getNextLeagueLevel,
} from 'src/replicant/match2/leagues/leagues.getters';
import {
  getNumRegions,
  getRegionId,
} from 'src/replicant/match2/regions/regions.getters';
import { withLoading } from 'src/lib/utils';
import { captureGenericError } from 'src/lib/sentry';
import { mapValidateDef } from './game/util/mapValidation';
import { getMatch2MaxLevel } from 'src/replicant/match2/match2.getters';
import { Region } from 'src/replicant/match2/regions/regions.ruleset';
import { Feature } from 'src/replicant/analytics';
import { getPuzzleMoves } from './movereduction/movereduction.utils';

const editorLevelOverrides = new Map<number, MapDef>();

/**
 * Load match2 map assets
 */
export async function loadRegionAssets(retries = 0): Promise<void> {
  // Load all regions if in editor mode.
  if (isEditorMode()) {
    const regions = getNumRegions();
    const regionsAssets = [];
    for (let i = 1; i <= regions; i++) {
      regionsAssets.push(
        `assets/minigame/match2/puzzle/levels/region_${i}/region_${i}.json`,
      );
    }
    await new AssetGroup(regionsAssets).load();
    return;
  }

  // Get region id for current level.
  const regionId = getRegionId(user());

  // Load region assets
  const filePath = getRegionPath(regionId);
  const loaded = (await new AssetGroup(
    AssetGroup.constructURLs(filePath),
  ).load()) as Record<string, unknown>;

  // If region assets are loaded, can return.
  if (loaded[filePath]) {
    return;
  }

  // If already tried to load the region assets 3 times, give up.
  if (retries > 2) {
    captureGenericError(
      `Could not load region assets for ${regionId}`,
      new Error(`File '${filePath}' not found`),
    );
    return;
  }

  // If region assets are not loaded, need to invalidate the current region and reload.
  await StateObserver.invoke.invalidateCurrentRegion();

  // Try to load again, with a retry count + 1.
  return loadRegionAssets(retries + 1);
}

/**
 * Get next region id for match2, based on current level.
 */
export function match2GetNextRegionId(): string {
  let nextLevel = user().match2.level + 1;

  // User is entering leagues, should get the first league level.
  if (user().match2.level === getMatch2MaxLevel(user())) {
    nextLevel = getLeagueLevel(user());
  }

  // User is playing leagues, should get next league level.
  if (user().match2.level > getMatch2MaxLevel(user())) {
    nextLevel = getNextLeagueLevel(user());
  }

  // Get region id for next level.getRegionId(user(), nextLevel)
  const regionId = getRegionId(user(), nextLevel);

  // Return region id.
  return regionId;
}

/**
 * Load next region assets
 */
export async function match2LoadNextRegionAssets(): Promise<void> {
  const regionId = match2GetNextRegionId();

  // Load region assets
  await new AssetGroup(
    AssetGroup.constructURLs(getRegionPath(regionId)),
  ).load();
}

export function getEditorOverride(level: number) {
  return editorLevelOverrides.get(level);
}

export function setEditorOverride(level: number, mapDef: MapDef): void {
  editorLevelOverrides.set(level, mapDef);
}

export function clearEditorOverride(level: number) {
  editorLevelOverrides.delete(level);
}

export function getEditorOverrides() {
  return editorLevelOverrides;
}

export function getEditorMaxLevel() {
  const maxOverrideLevel = Math.max(...editorLevelOverrides.keys());
  return Math.max(maxOverrideLevel, match2MaxLevel);
}

/**
 * Retrieve a puzzle map definition by level number
 */
export async function getPuzzleMap(
  level: number,
  originalOnly?: boolean,
): Promise<ExtendedMapDef> {
  // If the editor has saved something to this level, return it
  const editorOverride = editorLevelOverrides.get(level);
  if (editorOverride && !originalOnly) {
    return {
      ...editorOverride,
      levelVersion: 'editor',
      regionVersion: 'editor',
      levelHash: 'editor',
      regionHash: 'editor',
    };
  }

  await withLoading(loadRegionAssets, {
    feature: Feature.MATCH2._,
    subFeature: Feature.MATCH2.REGION_ASSETS,
  });

  const regionPath = getRegionPath(getRegionId(user(), level));
  const region = (await loader.loadJSON(regionPath)) as unknown as Region;

  if (!region) {
    throw new Error(`Could not load region ${regionPath}`);
  }

  const map = region.levels[level];
  const mapData = map?.data;

  if (!mapData) {
    throw new Error(`Could not find puzzle map for level ${level}`);
  }

  let mapDef: MapDef | undefined;

  if (mapData.data) {
    // Decode map data into mapDef
    mapDef = new PuzzleMapImporter(mapData.data).import();
  } else {
    // If there is no data field present, so probably this map is already a MapDef, for testing purposes only
    mapDef = mapData as unknown as MapDef;
  }

  // Validate resulting mapDef
  if (!mapDef) {
    throw new Error(`Could not import puzzle map for level ${level}`);
  }

  // Push blockGen value into mapDef, only if exists.
  if (mapData.blockGen) {
    mapDef.blockGen = mapData.blockGen;
  }

  if (!mapValidateDef(mapDef)) {
    throw new Error(`Decoded map is not valid ${level}`);
  }

  return {
    ...mapDef,
    regionVersion: region.version,
    levelVersion: map.data.version,
    levelHash: map.hash,
    regionHash: region.hash,
    moves: getPuzzleMoves(mapDef),
  };
}

/**
 * Get path for a given region id.
 * @param regionId
 */
export function getRegionPath(regionId: string) {
  const regionMatch = regionId.match(/region_[0-9]*/);

  if (!regionMatch || !regionMatch[0]) {
    throw new Error(`Invalid region id ${regionId}`);
  }

  return `assets/minigame/match2/puzzle/levels/${regionMatch[0]}/${regionId}.json`;
}
